import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Box } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDiscord, faXTwitter } from '@fortawesome/free-brands-svg-icons';

import {
  getNotificationContent,
  getNotificationTarget,
  getReceivedNotificationContent,
  getTimeAgo,
  hasNotificationThumbnail,
  isRankUpNotification,
  isSystemNotification,
  getAvatarUrl,
} from '../../helpers';
import { RankBadge } from '../RankBadge';
import { Notification } from '../../types';
import styles from './style';

const CLOUDFRONT_URL = process.env.REACT_APP_AWS_CLOUDFRONT_URL;

interface NotificationItemProps {
  notification: Notification;
  isNew: boolean;
  // Admin: shown as its receiver saw it, in his language, and not clickable.
  language?: string;
  onOpen?: (notification: Notification) => void;
  onOpenSender?: (notification: Notification) => void;
}

const NotificationItem: React.FC<NotificationItemProps> = React.memo((props) => {
  const {
    notification,
    isNew,
    language,
    onOpen,
    onOpenSender,
  } = props;
  const { t: appT, i18n } = useTranslation();
  const t = useMemo(() => (language ? i18n.getFixedT(language) : appT), [appT, i18n, language]);

  const isSystem = isSystemNotification(notification);
  const content = useMemo(() => (
    language ? getReceivedNotificationContent(notification, t) : getNotificationContent(notification, t)
  ), [language, notification, t]);
  const canOpen = !!onOpen && !!getNotificationTarget(notification);

  const avatarUrl = useMemo(() => {
    if (isSystem || !notification.sender) {
      return '';
    }
    return getAvatarUrl(notification.sender.avatar);
  }, [isSystem, notification.sender]);

  const onClick = useCallback(() => {
    if (onOpen) {
      onOpen(notification);
    }
  }, [notification, onOpen]);

  // The avatar opens the profile of the sender, not the video.
  const onClickAvatar = useCallback((event: React.MouseEvent) => {
    if (!isSystem && onOpenSender) {
      event.stopPropagation();
      onOpenSender(notification);
    }
  }, [isSystem, notification, onOpenSender]);

  // The invitations to X and Discord: the logo of the network instead of a thumbnail.
  const brandIcon = useMemo(() => {
    if (notification.type === 'joinX') return faXTwitter;
    if (notification.type === 'joinDiscord') return faDiscord;
    return null;
  }, [notification.type]);

  const rowSx = useMemo(() => {
    let sx = styles.row as { [key: string]: unknown };
    if (isNew) {
      sx = { ...sx, ...styles.rowNew };
    }
    if (canOpen) {
      sx = { ...sx, ...styles.rowClickable };
    }
    return sx;
  }, [canOpen, isNew]);

  return (
    <Box onClick={onClick} sx={rowSx}>
      {isSystem && (
        <Box sx={styles.logo}>
          <Box component="img" src="/logo-black.png" alt="" sx={styles.logoImg} />
        </Box>
      )}
      {!isSystem && (
        <Avatar alt="" src={avatarUrl} onClick={onClickAvatar} sx={styles.avatar} />
      )}
      <Box sx={styles.texts}>
        <Box component="span" sx={styles.name}>{content.title}</Box>
        <Box component="span" sx={styles.text}>
          {content.text}
          <Box component="span" sx={styles.when}>{getTimeAgo(notification.createdAt, t)}</Box>
        </Box>
      </Box>
      {isRankUpNotification(notification) && <RankBadge rank={notification.rank} />}
      {brandIcon && <Box sx={styles.brand}><FontAwesomeIcon icon={brandIcon} /></Box>}
      {!brandIcon && hasNotificationThumbnail(notification) && (
        <Box component="img" src={`${CLOUDFRONT_URL}${notification.thumbnail}`} alt="" sx={styles.thumb} />
      )}
      {isNew && <Box component="span" sx={styles.dot} />}
    </Box>
  );
});

export default NotificationItem;
